import {useMemo, useState} from "react";
import {ChevronDown} from "lucide-react";
import {
  Badge,
  Box,
  Button,
  Checkbox,
  Icon,
  Input,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Text,
  VStack,
} from "@chakra-ui/react";

export interface MultiSelectOption {
  value: string;
  label: string;
}

export function MultiSelect({
  label,
  options,
  value,
  onChange,
  searchPlaceholder,
  minW = "200px",
}: {
  label: string;
  options: MultiSelectOption[];
  value: string[];
  onChange: (values: string[]) => void;
  searchPlaceholder?: string;
  minW?: string;
}) {
  const [query, setQuery] = useState("");
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, query]);

  function toggle(v: string) {
    onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]);
  }

  return (
      <Popover placement="bottom-start" isLazy>
        <PopoverTrigger>
          <Button
              size="sm"
              variant="outline"
              minW={minW}
              textAlign="left"
              fontWeight="normal"
              rightIcon={<Icon as={ChevronDown} boxSize={4} />}
          >
            <Box as="span" display="inline-flex" alignItems="center" gap={2}>
              {label}
              {value.length > 0 && (
                  <Badge colorScheme="orange" borderRadius="full">
                    {value.length}
                  </Badge>
              )}
            </Box>
          </Button>
        </PopoverTrigger>

        <Portal>
          <PopoverContent w="auto" minW={minW} maxH="320px" overflowY="auto" p={2} zIndex="popover">
            <PopoverBody p={0}>
              {options.length > 8 && (
                  <Input
                      size="sm"
                      mb={2}
                      placeholder={searchPlaceholder}
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                  />
              )}
              <VStack align="stretch" spacing={1}>
                {filtered.length === 0 ? (
                    <Text fontSize="sm" color="gray.400" px={2} py={1}>
                      —
                    </Text>
                ) : (
                    filtered.map((o) => (
                        <Checkbox
                            key={o.value}
                            px={2}
                            py={1}
                            colorScheme="orange"
                            isChecked={value.includes(o.value)}
                            onChange={() => toggle(o.value)}
                        >
                          <Text fontSize="sm" noOfLines={1}>
                            {o.label}
                          </Text>
                        </Checkbox>
                    ))
                )}
              </VStack>
            </PopoverBody>
          </PopoverContent>
        </Portal>
      </Popover>
  );
}